import { useState } from "react";

import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { useDebouncedState } from "@shared";
import { useQuery } from "@tanstack/react-query";

import { ClientSearchResult, clientsApi } from "@/api";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { buildClientSearchArgs, formatClientOptionCaption, formatClientOptionLabel } from "./ClientSelect.utils";

const SearchDebounceMs = 300;

/**
 * The emitted selection: a linked client record, free-text (freeSolo only), or nothing.
 */
export type ClientSelection = ClientSearchResult | string | null;

interface Props {
  label: string;
  required?: boolean;
  freeSolo?: boolean;
  /**
   * Collapses the blank helper line the field reserves below the input. Set it where the select is
   * the last row of a form, so the reserved line does not stack onto the container's bottom padding.
   */
  disableHelperSpace?: boolean;
  onChange: (selection: ClientSelection) => void;
}

/**
 * Generic, Formik-agnostic client search/select. Owns its own display and search state and reports
 * the current selection through {@link Props.onChange}. Consumers decide what to do with it (bind a
 * form field, autofill related fields, navigate, …).
 */
export function ClientSelect({ label, required, freeSolo, disableHelperSpace, onChange }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();

  const {
    value: inputValue,
    debouncedValue: debouncedInputValue,
    setValue: setInputValue,
    setValueImmediate: setInputValueImmediate,
  } = useDebouncedState("", SearchDebounceMs);

  const [value, setValue] = useState<ClientSearchResult | string | null>(null);

  const { data: searchData } = useQuery({
    queryKey: [QueryKey.Clients, "client-select", debouncedInputValue],
    queryFn: () => clientsApi.searchClients(buildClientSearchArgs(debouncedInputValue, language)),
    enabled: debouncedInputValue.trim().length > 0,
  });

  const options = searchData?.data ?? [];

  return (
    <Autocomplete<ClientSearchResult, false, false, boolean>
      freeSolo={freeSolo}
      size="small"
      value={value}
      inputValue={inputValue}
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={(option) => (typeof option === "string" ? option : formatClientOptionLabel(option))}
      isOptionEqualToValue={(option, val) =>
        typeof option !== "string" && typeof val !== "string" && option.clientId === val.clientId
      }
      onChange={(_, newValue) => {
        setValue(newValue);
        if (newValue && typeof newValue !== "string") {
          setInputValueImmediate(formatClientOptionLabel(newValue));
          onChange(newValue);
        } else if (typeof newValue === "string") {
          const name = newValue.trim();
          setInputValueImmediate(newValue);
          onChange(name.length > 0 ? name : null);
        } else {
          setInputValue("");
          onChange(null);
        }
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "input") {
          setInputValue(newInputValue);
          if (freeSolo) {
            // Typing is free text: keep the name, drop any previously linked client.
            setValue(newInputValue);
            const name = newInputValue.trim();
            onChange(name.length > 0 ? name : null);
          }
        } else if (reason === "reset") {
          setInputValue(newInputValue);
        } else if (reason === "clear") {
          setInputValue("");
          setValue(null);
          onChange(null);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={disableHelperSpace ? undefined : " "}
          sx={{
            minWidth: "0em",
            "& .MuiInputBase-inputSizeSmall": { fontSize: "0.875rem" },
            "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input": { paddingY: "2px" },
          }}
          slotProps={{
            inputLabel: { sx: { fontSize: "0.875rem" }, required },
          }}
        />
      )}
      renderOption={(props, option) => {
        const caption = formatClientOptionCaption(option, language, t("assignments.clientSelect.bornOn"));
        return (
          <li {...props} key={option.clientId}>
            <Box>
              <Typography variant="body2">{formatClientOptionLabel(option)}</Typography>
              {caption && (
                <Typography variant="caption" color="text.secondary">
                  {caption}
                </Typography>
              )}
            </Box>
          </li>
        );
      }}
      noOptionsText={
        inputValue.trim()
          ? t("assignments.clientSelect.noResults", { query: inputValue })
          : t("assignments.clientSelect.searchHint")
      }
      fullWidth
    />
  );
}
